import { Link } from "react-router-dom";
import {
  ShieldCheck, Award, ShieldAlert, ExternalLink, Home,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import DomainBadge from "@/components/DomainBadge";
import DownloadButton from "@/components/DownloadButton";
import { certificateUrl, attemptPdfUrl } from "@/lib/api";
import { attemptPdfFilename } from "@/lib/ids";
import { useT } from "@/lib/i18n";

export default function ResultStep({ attempt }) {
  const { t, lang } = useT();
  const outcome = attempt.overall_outcome;
  const Icon = outcome === "Fail" ? ShieldAlert : outcome === "Merit" ? Award : ShieldCheck;
  const tone = outcome === "Fail" ? "tint-cs" : outcome === "Merit" ? "tint-ds" : "tint-ai";
  const verifyHref = attempt.license_id ? `/verify/${attempt.license_id}` : null;

  const langSuffix = lang === "ar" ? "?lang=ar" : "";
  // Pass/Merit → licence PDF; Fail → attempt-record PDF (the dedicated
  // attempt_pdf endpoint always returns the attempt record, never the licence).
  const url = attempt.license_id
    ? `${certificateUrl(attempt.license_id)}${langSuffix}`
    : `${attemptPdfUrl(attempt.id)}${langSuffix}`;
  const filename = attempt.license_id
    ? `${attempt.license_id}.pdf`
    : attemptPdfFilename(attempt.id);

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12" data-testid="result-step">
      <div className="flex items-start gap-3 flex-wrap">
        <div>
          <div className="label-mono">{t("result.badge")}</div>
          <h2
            className="font-display text-3xl md:text-5xl font-bold tracking-tighter mt-2"
            style={{ color: "var(--iddl-ink)" }}
          >
            {t("result.title")}
          </h2>
        </div>
        <Link to="/" className="ms-auto" data-testid="btn-exit-home">
          <Button variant="outline" className="rounded-none">
            <Home size={14} className="mr-2" /> {t("result.exit")}
          </Button>
        </Link>
      </div>

      <div
        className={`mt-8 paper p-8 border-2 ${outcome === "Fail" ? "bdr-cs" : outcome === "Merit" ? "bdr-ds" : "bdr-ai"}`}
        data-testid="result-card"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`inline-flex items-center gap-2 px-3 py-1 border ${tone} label-mono`}>
            <Icon size={14} /> {outcome?.toUpperCase()}
          </span>
          <span className="font-display text-base">{attempt.candidate_name}</span>
          {attempt.license_id && (
            <code className="font-mono-disp text-sm" data-testid="result-license-id">
              {attempt.license_id}
            </code>
          )}
        </div>

        <div className="mt-5 grid md:grid-cols-2 gap-4">
          <div>
            <div className="label-mono">
              {attempt.license_id ? t("result.license_issued") : t("result.no_license")}
            </div>
            <p className="text-sm mt-1 text-[var(--iddl-muted)]">
              {attempt.license_id ? t("result.license_body") : t("result.no_license_body")}
            </p>
          </div>
          <div className="md:text-right flex md:justify-end gap-2 flex-wrap">
            {attempt.license_id && verifyHref && (
              <Link to={verifyHref} data-testid="btn-public-verify">
                <Button variant="outline" className="rounded-none">
                  {t("result.btn_public_verify")} <ExternalLink size={12} className="ml-2" />
                </Button>
              </Link>
            )}
            {/* Single, visible download button — fetch+blob+anchor with
                state machine. The button changes appearance during the
                download (spinner) and on success (✓ green) so the user
                always sees what's happening. */}
            <DownloadButton
              url={url}
              filename={filename}
              label={t("result.btn_download_license")}
              data-testid="btn-download-pdf"
            />
          </div>
        </div>
      </div>

      <AttemptTimeline t={t} attempt={attempt} />

      <h3 className="font-display text-xl font-semibold mt-12" style={{ color: "var(--iddl-ink)" }}>
        {t("result.breakdown")}
      </h3>
      <p className="text-sm text-[var(--iddl-muted)] mt-1">{t("result.breakdown_sub")}</p>

      <div className="mt-4 grid gap-3">
        {(attempt.concept_results || []).map((r, i) => r && (
          <ConceptReviewCard key={i} r={r} t={t} />
        ))}
      </div>

      <div className="mt-12 flex justify-end">
        <Link to="/" data-testid="btn-exit-home-bottom">
          <Button variant="outline" className="rounded-none">
            <Home size={14} className="mr-2" /> {t("result.exit")}
          </Button>
        </Link>
      </div>
    </div>
  );
}

function AttemptTimeline({ t, attempt }) {
  const started = attempt.started_at;
  const finished = attempt.completed_at;
  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      });
    } catch { return iso; }
  };
  let duration = "—";
  if (started && finished) {
    const ms = Math.max(0, new Date(finished) - new Date(started));
    const s = Math.floor(ms / 1000);
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    duration = `${mm}m ${ss}s`;
  }
  return (
    <div className="mt-6 paper p-4 grid md:grid-cols-3 gap-4" data-testid="result-timeline">
      <TimelineCell label={t("result.started_at")} value={fmt(started)} />
      <TimelineCell label={t("result.completed_at")} value={fmt(finished)} />
      <TimelineCell label={t("result.duration")} value={duration} />
    </div>
  );
}

function TimelineCell({ label, value }) {
  return (
    <div>
      <div className="label-mono">{label}</div>
      <div className="font-mono-disp text-sm mt-1" style={{ color: "var(--iddl-ink)" }}>{value}</div>
    </div>
  );
}

function ConceptReviewCard({ r, t }) {
  const fb = r.behavioural_feedback;
  return (
    <div className="paper p-5" data-testid={`result-concept-${r.concept_id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <DomainBadge domain={r.domain} />
        <span className="font-display font-semibold">{r.title}</span>
        <code className="label-mono">{r.concept_id}</code>
        {fb?.environment_label && (
          <span className="label-mono text-[var(--iddl-muted)]" data-testid={`result-env-${r.concept_id}`}>
            {fb.environment_label}
          </span>
        )}
        {r.outcome && (
          <span
            className={`label-mono ms-auto px-2 py-0.5 border ${r.outcome === "Fail" ? "tint-cs" : r.outcome === "Merit" ? "tint-ds" : "tint-ai"}`}
          >
            {r.outcome}
          </span>
        )}
      </div>
      <div className="grid md:grid-cols-3 mt-3 border border-[var(--iddl-rule)]">
        <Cell title={t("result.know")} l={r.knowledge} />
        <Cell title={t("result.perform")} l={r.capability} />
        <Cell title={t("result.reason")} l={r.reflection} />
      </div>
      {fb?.behavioural_evidence && (
        <div className="mt-4 pt-4 border-t border-[var(--iddl-rule)]" data-testid={`result-feedback-${r.concept_id}`}>
          <div className="label-mono text-[var(--iddl-muted)] mb-1">{t("result.behavioural_evidence")}</div>
          <p className="text-sm leading-relaxed" data-testid={`result-evidence-${r.concept_id}`}>
            {fb.behavioural_evidence}
          </p>
        </div>
      )}
      {r.outcome && <Remediation r={r} title={t("result.remediation")} />}
    </div>
  );
}

// Two-part remediation: (1) personalised one-sentence summary built from
// this candidate's behaviour log + reflection score, (2) the standard
// learning-module reference.
function Remediation({ r, title }) {
  return (
    <div
      className="mt-4 border-s-4 border-[var(--iddl-gold)] bg-[var(--iddl-surface)] p-4"
      data-testid={`result-personalised-remediation-${r.concept_id}`}
    >
      <div className="label-mono text-[var(--iddl-muted)] mb-2">{title}</div>
      <p className="text-sm leading-relaxed font-semibold" data-testid={`result-pr-reasons-${r.concept_id}`}>
        {r.remediation_summary}
      </p>
      <p className="text-sm leading-relaxed mt-2" data-testid={`result-pr-module-${r.concept_id}`}>
        {r.module_reference}
      </p>
    </div>
  );
}

function Cell({ title, l }) {
  return (
    <div className="p-4 border-r border-b border-[var(--iddl-rule)] last:border-r-0">
      <div className="label-mono">{title}</div>
      {l ? (
        <>
          <div className="font-display text-xl font-semibold mt-1">{l.score}/{l.max_score}</div>
          <div className={`label-mono mt-1 ${l.outcome === "Fail" ? "text-cs" : l.outcome === "Merit" ? "text-ds" : "text-ai"}`}>
            {l.outcome}
          </div>
        </>
      ) : (
        <div className="label-mono mt-2 text-[var(--iddl-muted)]">—</div>
      )}
    </div>
  );
}
